/**
 * FindingsPanel / Scope Intelligence
 *
 * Scrollable right-column panel: verdict, summary, durable cards, in-turn flash.
 * Pure presentation — all business logic handled by wrappers.
 *
 * @module @ideoz/ui/components/organisms/FindingsPanel
 */

import React from 'react';
import { Sparkles, PanelRightClose } from 'lucide-react';
import type { Finding } from '@ideoz/core';
import { Skeleton } from '../atoms/skeleton';
import { FindingVerdictBar } from '../molecules/FindingVerdictBar';
import { FindingCard } from '../molecules/FindingCard';
import { FindingFlashCard } from '../molecules/FindingFlashCard';
import { cn } from '../../lib/utils';
import type { FindingsPanelProps } from '../../types/Findings.types';

/** Cards are ordered by creation time, newest first. */
const sortFindingsByCreatedAt = (findings: Finding[]): Finding[] =>
  [...findings].sort(
    (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
  );

/**
 * FindingsPanel Component
 *
 * @example
 * ```tsx
 * <FindingsPanel
 *   findings={findings}
 *   verdict={verdict}
 *   summary={summary}
 *   onAction={handleResolve}
 * />
 * ```
 */
export const FindingsPanel: React.FC<FindingsPanelProps> = ({
  findings,
  summary,
  verdict,
  inTurnFlash = [],
  isLoading = false,
  isResolving = false,
  resolvingFindingId = null,
  error = null,
  includeDismissed = false,
  onIncludeDismissedChange,
  onAction,
  onCollapse,
  className = '',
  testId = 'findings-panel',
}) => {
  return (
    <aside data-testid={testId} className={cn('flex h-full min-h-0 flex-col', className)}>
      {/* Fixed header — stays put while the cards below scroll. */}
      <div className="flex shrink-0 flex-col gap-3 pb-4">
        <div className="flex items-start justify-between gap-2">
          <div className="flex min-w-0 items-center gap-2">
            <span className="mt-0.5 flex size-8 items-center justify-center rounded-lg bg-accent text-accent-foreground">
              <Sparkles className="h-4 w-4" strokeWidth={2} />
            </span>
            <div className="min-w-0">
              <h2 className="text-sm font-medium leading-tight text-gray-900 dark:text-gray-100">
                Scope Intelligence
              </h2>
              {summary && (
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {summary.total} {summary.total === 1 ? 'finding' : 'findings'}
                  {summary.unreviewed > 0 && (
                    <span className="text-primary-600"> · {summary.unreviewed} unreviewed</span>
                  )}
                </p>
              )}
            </div>
          </div>
          {onCollapse && (
            <button
              type="button"
              onClick={onCollapse}
              aria-label="Collapse panel"
              className="shrink-0 rounded-md p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-white/10 dark:hover:text-gray-200"
              data-testid="findings-collapse"
            >
              <PanelRightClose className="h-4 w-4" strokeWidth={2} />
            </button>
          )}
        </div>

        {onIncludeDismissedChange && (
          <button
            type="button"
            role="switch"
            aria-checked={includeDismissed}
            onClick={() => onIncludeDismissedChange(!includeDismissed)}
            className="flex w-fit items-center gap-2 text-xs font-medium text-gray-500 transition-colors hover:text-gray-700 focus-visible:outline-none dark:text-gray-400 dark:hover:text-gray-200"
            data-testid="findings-include-dismissed"
          >
            <span
              className={cn(
                'relative inline-flex h-[18px] w-8 shrink-0 items-center rounded-full transition-colors',
                includeDismissed ? 'bg-primary-500' : 'bg-gray-200 dark:bg-white/15'
              )}
            >
              <span
                className={cn(
                  'inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow-sm transition-transform',
                  includeDismissed ? 'translate-x-[15px]' : 'translate-x-0.5'
                )}
              />
            </span>
            Show dismissed
          </button>
        )}
      </div>

      {/* Scroll region — only the cards scroll. */}
      <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto scrollbar-chat pr-1">
        {isLoading && (
          <div className="space-y-3" data-testid="findings-panel-loading">
            <Skeleton className="h-14 w-full rounded-lg" />
            <Skeleton className="h-24 w-full rounded-lg" />
            <Skeleton className="h-24 w-full rounded-lg" />
          </div>
        )}

        {!isLoading && error && (
          <p className="text-sm text-error-700" data-testid="findings-panel-error">
            {error}
          </p>
        )}

        {!isLoading && !error && verdict && <FindingVerdictBar verdict={verdict} />}

        {!isLoading && !error && inTurnFlash.length > 0 && (
          <section aria-label="This turn findings" className="space-y-2">
            <h3 className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
              Surfaced this turn
            </h3>
            {inTurnFlash.map((flash) => (
              <FindingFlashCard key={flash.id} flash={flash} />
            ))}
          </section>
        )}

        {!isLoading && !error && findings.length === 0 && inTurnFlash.length === 0 && (
          <p className="text-sm text-gray-500 text-center dark:text-gray-400" data-testid="findings-panel-empty">
            No findings yet. They appear as the conversation surfaces signals.
          </p>
        )}

        {!isLoading && !error && findings.length > 0 && (
          <div className="space-y-3" data-testid="findings-list">
            {sortFindingsByCreatedAt(findings).map((finding) => (
              <FindingCard
                key={finding.id}
                finding={finding}
                onAction={onAction}
                isResolving={resolvingFindingId ? resolvingFindingId === finding.id : isResolving}
              />
            ))}
          </div>
        )}
      </div>
    </aside>
  );
};

export default FindingsPanel;
